import type { MouseEvent as ReactMouseEvent } from 'react';
import { lazy, Suspense, useCallback, useRef, useState } from 'react';
import { ArrowDown, FileText } from 'lucide-react';

import { cn } from '@rocco/utils/cn';

/** How far from the pointer the card sits, and how far off the screen edges. */
const CURSOR_OFFSET = 20;
const EDGE_MARGIN = 8;

/**
 * The resolution the PDF page is rasterised at.
 *
 * Deliberately wider than the card, which is 15rem against a 12px root and so
 * lands at 180px: pdf.js draws to a bitmap of exactly this width, and the
 * canvas is then scaled down into the card, which is what keeps the page's
 * small type legible. The scaling is CSS's job — react-pdf writes the drawn
 * width onto the canvas inline, so the frame has to override it.
 */
const CARD_WIDTH = 240;

/** pdf.js is a large chunk, so it arrives with the warm rather than the page. */
const CursorPdfPage = lazy(() => import('./CursorPdfPage'));

export interface CursorFilePreviewItem {
    /**
     * A PDF whose first page is the preview. Used where ArchiPro generates the
     * file itself and can therefore show the file rather than a stand-in.
     */
    pdfUrl?: string;
    /** Stand-in picture, shown when there is no PDF to draw. */
    imageSrc?: string;
    /** The row's name, under the picture. */
    title: string;
    /** The file's type, e.g. `PDF`. */
    format?: string;
}

/**
 * A preview card that follows the pointer while a row is hovered.
 *
 * The card's position is written straight to the node on each pointer move.
 * Putting the coordinates in React state re-renders the card for every event
 * the browser can deliver, which is what makes this kind of thing lag behind
 * the cursor it is supposed to be attached to.
 *
 * It stays mounted and hidden rather than mounting on hover, so the first
 * pointer move has a node to position and the card cannot appear at the top
 * left corner for a frame on its way to the cursor.
 *
 * Where a row carries a PDF this draws the PDF's own first page. Everywhere
 * else it falls back to the picture it was given, marked with a file glyph so
 * the reader can tell a stand-in from the document itself.
 *
 * Returns handlers to spread onto each row and onto the list around them, the
 * card to render once beside that list, and a `warm` the caller triggers as
 * early in the page's life as it can afford.
 */
export const useCursorFilePreview = () => {
    const cardRef = useRef<HTMLDivElement>(null);
    // The sheet the warm last ran for, which is what says whether a call is a
    // repeat or a new subject. `null` until the first run, so a list with no
    // sheet at all still warms its pictures once.
    const warmedFor = useRef<string | null>(null);
    // What the card is showing, and whether it is on screen. Two pieces of
    // state rather than one nullable item, because the card keeps drawing the
    // last row it was given while it fades out — clearing the content instead
    // collapsed the caption to nothing and shrank the card mid-fade.
    const [item, setItem] = useState<CursorFilePreviewItem | null>(null);
    const [visible, setVisible] = useState(false);
    // The sheet the list warmed. Held in state rather than a ref because it is
    // what mounts the PDF layer, which is how the page gets drawn before any
    // row is hovered.
    const [warmedUrl, setWarmedUrl] = useState<string>();
    // The PDF whose first page is on the canvas. Kept as the URL rather than a
    // flag so moving to a row without a PDF and back does not read as "not
    // drawn yet" and flash the stand-in over a page that is already there.
    const [drawnUrl, setDrawnUrl] = useState<string>();

    const place = useCallback((pointerX: number, pointerY: number) => {
        const card = cardRef.current;
        if (!card) return;

        const { offsetWidth: width, offsetHeight: height } = card;
        // Beside the pointer and centred on it, so the card reads as attached
        // to the cursor rather than hanging off its corner.
        let left = pointerX + CURSOR_OFFSET;
        let top = pointerY - height / 2;

        // Flip to the pointer's other side rather than hanging off the screen.
        if (left + width > window.innerWidth - EDGE_MARGIN) {
            left = pointerX - width - CURSOR_OFFSET;
        }
        // Held inside the screen top and bottom, which is the one case where
        // it stops tracking the pointer exactly.
        top = Math.min(
            Math.max(EDGE_MARGIN, top),
            Math.max(EDGE_MARGIN, window.innerHeight - height - EDGE_MARGIN),
        );

        card.style.transform = `translate3d(${Math.round(left)}px, ${Math.round(top)}px, 0)`;
    }, []);

    /**
     * Handlers for one row.
     *
     * A row does not hide the card when the pointer leaves it. Crossing from
     * one row to the next fires that leave before the next row's enter, so
     * hiding there faded the card out and straight back in on every boundary —
     * a flash on the way down a list of five. The card is hidden by the list,
     * once, when the pointer actually leaves it.
     *
     * A row with no file behind it is passed `null` and still takes the enter:
     * it has nothing to preview, so it is what puts the card away mid-list.
     */
    const bindRow = useCallback(
        (row: CursorFilePreviewItem | null) => ({
            onMouseEnter: (event: ReactMouseEvent) => {
                if (!row) {
                    setVisible(false);
                    return;
                }
                place(event.clientX, event.clientY);
                setItem(row);
                setVisible(true);
            },
            onMouseMove: (event: ReactMouseEvent) => {
                if (!row) return;
                place(event.clientX, event.clientY);
            },
        }),
        [place],
    );

    /** Handlers for the list the rows sit in. Hides the card on the way out. */
    /**
     * Puts the card away without waiting for the pointer to leave the list.
     * A control inside a row wants the card gone while it is being used: the
     * card follows the cursor, so it would otherwise sit over the control.
     */
    const hide = useCallback(() => setVisible(false), []);

    const bindList = useCallback(
        () => ({ onMouseLeave: () => setVisible(false) }),
        [],
    );

    /**
     * Does everything the first hover would otherwise wait on: fetches the
     * stand-in pictures, and mounts the PDF layer so the pdf.js chunk, its
     * worker, the sheet and the draw all happen before a row is under the
     * pointer.
     *
     * Call it as early in the page's life as the caller can afford. The sheet
     * is rendered per request — a GraphQL query, the hero image and a PDF
     * render — and comes back in a second or two, which is longer than the walk
     * from anywhere on the page to a row. Starting it on the list's own hover
     * was not early enough: the card still opened on the stand-in and cut to
     * the page a beat later.
     *
     * Repeat calls for the same sheet are free, and a call naming a different
     * one warms again. That is not a nicety: a detail page can change the
     * subject under this hook without remounting it — the next-product turn
     * navigates and leaves the section standing — and a warm that only ever ran
     * once would hold the previous product's sheet for the rest of the session,
     * putting the wait back on the first hover of every page after the first.
     *
     * It does nothing at all where the card cannot be seen: the card is
     * desktop-only and follows a pointer, so on a device that cannot hover this
     * would be a megabyte of PDF and a pdf.js chunk fetched for a card that
     * never opens.
     */
    const warm = useCallback((pdfUrl?: string, imageSrcs?: string[]) => {
        if (!window.matchMedia('(hover: hover)').matches) return;
        // Empty string for a list with no sheet, so "no sheet" is a subject the
        // ref can hold and not the same value as "never warmed".
        const subject = pdfUrl ?? '';
        if (warmedFor.current === subject) return;
        warmedFor.current = subject;
        // Decoded into the image cache, so the stand-in is painted rather than
        // fetched at the moment the card appears.
        imageSrcs?.forEach((src) => {
            const image = new Image();
            image.src = src;
        });
        setWarmedUrl(pdfUrl);
    }, []);

    // The stand-in, and what the PDF falls back to while its first page is
    // still being drawn.
    const picture = (
        <div className="relative">
            {item?.imageSrc ? (
                <img
                    src={item.imageSrc}
                    alt=""
                    // Warming already put this in the cache, and deferring it
                    // here would put the fetch back on the hover it was moved
                    // off.
                    decoding="async"
                    className="aspect-square w-full bg-[#f5f5f7] object-cover"
                />
            ) : (
                <div className="aspect-square w-full bg-[#f5f5f7]" />
            )}
            {/* Marks the picture as a stand-in for the file rather than the
                file itself. */}
            <span className="absolute top-[0.625rem] right-[0.625rem] flex size-[1.75rem] items-center justify-center rounded-[0.5rem] bg-white/95 shadow-[0_0.125rem_0.5rem_rgba(0,0,0,0.12)]">
                <FileText
                    aria-hidden="true"
                    className="size-[0.875rem] text-[#323232]"
                    strokeWidth={1.75}
                />
            </span>
        </div>
    );

    // The hovered row's sheet, falling back to the warmed one so the layer
    // stays mounted while the pointer is elsewhere in the list.
    const pdfUrl = item?.pdfUrl ?? warmedUrl;
    const showPdf = Boolean(item?.pdfUrl);
    const pdfDrawn = Boolean(pdfUrl) && drawnUrl === pdfUrl;

    const preview = (
        <div
            ref={cardRef}
            aria-hidden="true"
            className={cn(
                // Positioned by transform from the top left, so one write per
                // pointer move moves it.
                'pointer-events-none fixed top-0 left-0 z-[60] w-[15rem] max-md:hidden',
                'overflow-hidden rounded-[0.75rem] bg-white',
                'shadow-[0_1rem_2.5rem_rgba(0,0,0,0.22)]',
                'transition-opacity duration-150 motion-reduce:transition-none',
                visible ? 'opacity-100' : 'opacity-0',
            )}
        >
            <div
                className={cn(
                    'relative w-full overflow-hidden bg-[#f5f5f7]',
                    // One shape for the whole list, decided by whether the list
                    // has a sheet in it at all rather than by the row under the
                    // pointer. Switching per row resized the card mid-move —
                    // and because the card is centred on the pointer and only
                    // repositioned on the next move, it jumped as well as
                    // resized. A photograph is cropped to the page's
                    // proportions instead, which costs less than the jump.
                    pdfUrl ? 'aspect-[1/1.414]' : 'aspect-square',
                )}
            >
                {(!showPdf || !pdfDrawn) && (
                    // The stand-in is stretched to the frame rather than left
                    // at its own square: inside the taller PDF frame it was
                    // sitting short, leaving a band of empty grey between the
                    // picture and the caption.
                    <div className="absolute inset-0 [&_img]:size-full [&_img]:object-cover [&>div]:size-full">
                        {picture}
                    </div>
                )}
                {pdfUrl && (
                    // Mounted from the warm, and never unmounted: taking it out
                    // between hovers would throw the drawn page away and make
                    // every return to the row wait for the draw again.
                    <div
                        className={cn(
                            // Important, because pdf.js writes the drawn size
                            // onto the canvas as an inline style — without it
                            // the page is laid out at its raster width and the
                            // card crops the right-hand side off it.
                            'absolute inset-0 [&_canvas]:block [&_canvas]:h-auto! [&_canvas]:w-full!',
                            // Hidden on the same terms the stand-in is shown
                            // on, because this layer is over it. Keyed on the
                            // hovered row alone, a row whose sheet had not been
                            // drawn yet showed the last sheet that had, on top
                            // of the stand-in that was standing in for it.
                            !(showPdf && pdfDrawn) && 'invisible',
                        )}
                    >
                        <Suspense fallback={null}>
                            <CursorPdfPage
                                url={pdfUrl}
                                width={CARD_WIDTH}
                                onReady={() => setDrawnUrl(pdfUrl)}
                            />
                        </Suspense>
                    </div>
                )}
            </div>
            <div className="flex items-center gap-2 px-[0.875rem] py-[0.75rem]">
                <div className="min-w-0 flex-1">
                    <p className="m-0 truncate font-sans text-[0.875rem] leading-[1.3] font-medium text-[#323232]">
                        {item?.title}
                    </p>
                    {item?.format && (
                        <p className="m-0 truncate font-sans text-[0.8125rem] leading-[1.3] text-[#767676]">
                            {item.format}
                        </p>
                    )}
                </div>
                <ArrowDown
                    aria-hidden="true"
                    className="size-[1rem] shrink-0 text-[#767676]"
                />
            </div>
        </div>
    );

    return { bindRow, bindList, preview, warm, hide };
};
